/**
 * AuthProvider — Phase B.5
 *
 * Owns the current-user state for the whole app. Surfaces:
 *   - user        : User | null when logged out
 *   - loading     : true during the initial /auth/me probe
 *   - login()     : redirects to Emergent Auth
 *   - logout()    : calls backend + clears state + redirects to /login
 *   - role-aware helpers: isAdmin, isDispatcher, isSales, isHost,
 *     canAccess(routePath)
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { authMe, authLogout } from "../lib/api";

const AuthCtx = createContext(null);

// Default landing path per role — referenced by ProtectedRoute + login flow.
export const ROLE_LANDING = {
  admin: "/dashboard",
  dispatcher: "/bus-tastings",
  sales: "/dispensaries",
  lead_host: "/my-tastings",
  host: "/my-tastings",
  driver: "/my-tastings",
};

// Which routes each role can access. Listed routes are reachable; any
// unlisted route falls back to the role's landing page.
const ROLE_ACCESS = {
  admin: new Set([
    "/dashboard", "/dispensaries", "/map", "/bus-tastings",
    "/lead-generator", "/snapshots", "/scan-history", "/settings",
    "/admin/users", "/my-tastings", "/recap", "/partners", "/trips",
  ]),
  dispatcher: new Set([
    "/dispensaries", "/map", "/bus-tastings",
    "/lead-generator", "/snapshots", "/scan-history", "/settings",
    "/my-tastings", "/partners", "/trips",
  ]),
  sales: new Set(["/dispensaries", "/my-tastings", "/partners"]),
  lead_host: new Set(["/my-tastings", "/partners", "/trips"]),
  host: new Set(["/my-tastings", "/partners", "/trips"]),
  driver: new Set(["/my-tastings", "/trips"]),
};export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await authMe();
      setUser(me);
      return me;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /auth/me — AuthCallback
    // handles the session_id exchange first. Otherwise we'd race and 401.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(() => {
    // Use the browser's actual origin so this works on preview AND prod.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } catch (err) {
      // Server-side logout failed (offline, expired session, etc.) — log
      // for debugging but proceed with client-side state clear so the
      // user is never stuck on a "logging out…" screen.
      // eslint-disable-next-line no-console
      console.warn("Server logout failed; clearing local session anyway", err);
    }
    setUser(null);
    window.location.href = "/login";
  }, []);

  const role = user?.role || null;
  const canAccess = useCallback(
    (path) => {
      if (!user) return false;
      const allowed = ROLE_ACCESS[user.role] || new Set();
      // Allow nested routes (e.g. /dispensaries/abc → matches /dispensaries).
      return Array.from(allowed).some(
        (base) => path === base || path.startsWith(base + "/")
      );
    },
    [user]
  );

  const value = {
    user,
    loading,
    role,
    isAdmin: role === "admin",
    isDispatcher: role === "dispatcher",
    isSales: role === "sales",
    isHost: role === "host" || role === "lead_host",
    isLeadHost: role === "lead_host",
    login,
    logout,
    refresh,
    setUser,
    canAccess,
    landingPath: role ? ROLE_LANDING[role] : "/login",
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
